<script setup lang="ts">
/**
 * Drawer-container for the withdraw-requests queue (per
 * `architecture-principles.md §20`).
 *
 * Owns the drawer chrome, the detail query, the three action mutations
 * (approve, mark-settled, reject), the corresponding confirmation
 * dialogs, and the treasury-bank-account picker query that feeds the
 * approve dialog. The queue container only needs to forward the
 * selected row via `selectedRow` and listen for `@close`.
 */
import { computed, ref, watch } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import Avatar from '@uiKit/components/Avatar.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'

import { useAppToast } from '@shared/composables/useAppToast'
import { getIdempotencyKey } from '@shared/utils'

import WithdrawRequestDetail from '../components/WithdrawRequestDetail.vue'
import ApproveWithdrawDialog from '../components/ApproveWithdrawDialog.vue'
import MarkSettledDialog from '../components/MarkSettledDialog.vue'
import RejectWithdrawDialog from '../components/RejectWithdrawDialog.vue'

import { useWithdrawRequestDetailQuery } from '../api/queries/useWithdrawRequestDetailQuery'
import { useApproveWithdrawRequestMutation } from '../api/mutations/useApproveWithdrawRequestMutation'
import { useMarkSettledWithdrawRequestMutation } from '../api/mutations/useMarkSettledWithdrawRequestMutation'
import { useRejectWithdrawRequestMutation } from '../api/mutations/useRejectWithdrawRequestMutation'
import { getWithdrawStatusColor, getWithdrawStatusLabel } from '../utils/withdrawRequestStatusUtils'
import { WITHDRAW_REQUEST_STATUS, type WithdrawRequestRow } from '../types/withdrawRequestsTypes'

import { TREASURY_BANK_ACCOUNT_STATUS, useTreasuryBankAccountsQuery } from '../../treasuryAccounts'

const props = defineProps<{
  selectedRow: WithdrawRequestRow | null
}>()

const emit = defineEmits<{
  close: []
}>()

// ─── Drawer state + detail query ────────────────────────────────────────────

const selectedId = computed(() => props.selectedRow?.id ?? '')

const idempotencyKey = ref('')
watch(
  () => props.selectedRow,
  (row) => {
    idempotencyKey.value = row ? getIdempotencyKey() : ''
  },
)

const { data: details, isLoading: detailsLoading } = useWithdrawRequestDetailQuery(selectedId)

const drawerVisible = computed(() => props.selectedRow !== null)

function closeDrawer() {
  emit('close')
}

// ─── Display ────────────────────────────────────────────────────────────────

const drawerSubtitle = computed(() => props.selectedRow?.investorEmail ?? '')
const drawerInvestorName = computed(() => {
  if (details.value?.investor) {
    const first = details.value.investor.firstName ?? ''
    const last = details.value.investor.lastName ?? ''
    return `${first} ${last}`.trim() || props.selectedRow?.investorName || ''
  }
  return props.selectedRow?.investorName || ''
})

// ─── Treasury bank account picker (feeds approve dialog) ────────────────────

const treasuryAccountParams = computed(() => ({
  status: TREASURY_BANK_ACCOUNT_STATUS.ACTIVE,
  currencyCode: details.value?.currencyCode ?? '',
}))
const { data: treasuryAccounts, isLoading: treasuryAccountsLoading } =
  useTreasuryBankAccountsQuery(treasuryAccountParams)

// ─── Mutations + dialog state ───────────────────────────────────────────────

const { addToast } = useAppToast()

const approveDialogVisible = ref(false)
const settleDialogVisible = ref(false)
const rejectDialogVisible = ref(false)

const {
  mutate: approveMutate,
  isPending: isApproving,
  error: approveError,
  reset: resetApprove,
} = useApproveWithdrawRequestMutation()
const {
  mutate: settleMutate,
  isPending: isSettling,
  error: settleError,
  reset: resetSettle,
} = useMarkSettledWithdrawRequestMutation()
const {
  mutate: rejectMutate,
  isPending: isRejecting,
  error: rejectError,
  reset: resetReject,
} = useRejectWithdrawRequestMutation()

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

const approveErrorMessage = computed(() => describeError(approveError.value))
const settleErrorMessage = computed(() => describeError(settleError.value))
const rejectErrorMessage = computed(() => describeError(rejectError.value))

function openApprove() {
  resetApprove()
  approveDialogVisible.value = true
}
function openSettle() {
  resetSettle()
  settleDialogVisible.value = true
}
function openReject() {
  resetReject()
  rejectDialogVisible.value = true
}

function handleApproveConfirm(payload: { treasuryBankAccountId: string }) {
  if (!selectedId.value) return
  approveMutate(
    {
      id: selectedId.value,
      treasuryBankAccountId: payload.treasuryBankAccountId,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Withdraw approved',
          message: 'Awaiting bank settlement.',
        })
        approveDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

function handleSettleConfirm(payload: { settlementReference: string }) {
  if (!selectedId.value) return
  settleMutate(
    {
      id: selectedId.value,
      settlementReference: payload.settlementReference,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Marked settled',
          message: 'The investor wallet has been debited.',
        })
        settleDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

function handleRejectConfirm(payload: { reason: string }) {
  if (!selectedId.value) return
  rejectMutate(
    {
      id: selectedId.value,
      reason: payload.reason,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Withdraw rejected',
          message: 'The locked balance has been returned to the investor wallet.',
        })
        rejectDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

const showApproveRejectFooter = computed(
  () => details.value?.status === WITHDRAW_REQUEST_STATUS.PENDING_APPROVAL,
)
const showMarkSettledFooter = computed(
  () =>
    details.value?.status === WITHDRAW_REQUEST_STATUS.AWAITING_SETTLEMENT ||
    details.value?.status === WITHDRAW_REQUEST_STATUS.APPROVED,
)
</script>

<template>
  <AppDrawer
    :model-value="drawerVisible"
    position="right"
    width="540px"
    :show-close-icon="false"
    :loading="detailsLoading"
    :dismissable="!isApproving && !isSettling && !isRejecting"
    @update:model-value="(v) => !v && closeDrawer()"
  >
    <template #header>
      <DrawerHeader
        :title="drawerInvestorName"
        :subtitle="drawerSubtitle"
        :disable-close="isApproving || isSettling || isRejecting"
        @close="closeDrawer"
      >
        <template #avatar>
          <Avatar :id="selectedRow?.investorId ?? ''" :name="drawerInvestorName" />
        </template>
        <template #extras>
          <StatusBadge
            v-if="details"
            :color="getWithdrawStatusColor(details.status)"
            :label="getWithdrawStatusLabel(details.status)"
          />
        </template>
      </DrawerHeader>
    </template>

    <Box v-if="details" padding="4">
      <WithdrawRequestDetail :details="details" />
    </Box>

    <template v-if="details && (showApproveRejectFooter || showMarkSettledFooter)" #footer>
      <Box class="drawer-footer" flex justify="end" gap="2">
        <template v-if="showApproveRejectFooter">
          <Button
            label="Reject"
            color="danger"
            variant="outline"
            :disabled="isApproving"
            @click="openReject"
          />
          <Button label="Approve" color="primary" :disabled="isRejecting" @click="openApprove" />
        </template>
        <template v-else-if="showMarkSettledFooter">
          <Button label="Mark settled" color="primary" @click="openSettle" />
        </template>
      </Box>
    </template>
  </AppDrawer>

  <ApproveWithdrawDialog
    v-if="details"
    :visible="approveDialogVisible"
    :currency-code="details.currencyCode"
    :accounts="treasuryAccounts ?? []"
    :accounts-loading="treasuryAccountsLoading"
    :loading="isApproving"
    :api-error="approveDialogVisible ? approveErrorMessage : null"
    @update:visible="approveDialogVisible = $event"
    @confirm="handleApproveConfirm"
  />

  <MarkSettledDialog
    :visible="settleDialogVisible"
    :loading="isSettling"
    :api-error="settleDialogVisible ? settleErrorMessage : null"
    @update:visible="settleDialogVisible = $event"
    @confirm="handleSettleConfirm"
  />

  <RejectWithdrawDialog
    :visible="rejectDialogVisible"
    :loading="isRejecting"
    :api-error="rejectDialogVisible ? rejectErrorMessage : null"
    @update:visible="rejectDialogVisible = $event"
    @confirm="handleRejectConfirm"
  />
</template>

<style scoped>
.drawer-footer {
  width: 100%;
  padding: 0.25rem 0;
}
</style>
